/**
 * The funder ledger: the single source of truth for how each dollar the
 * algorithm allocated splits into standard and matching money, and which
 * funder it belongs to.
 *
 * The recommender layer solves twice per budget portion (one recommender's
 * pooled funding): phase 1 with the portion's
 * standard budget on standard MVFs, phase 2 with its full budget on matching
 * MVFs, seeded with phase 1's guarantees. Guarantees only grow between the
 * phases, but *accounts* (dollars actually paid) can fall: when another
 * portion raises its guarantee on a shared proposal, the cost of the levels
 * below is shared with it and this portion's bill drops, freeing standard
 * dollars that advance down its list. So "phase 2 minus phase 1" on an edge
 * is not matching money — it also contains reshuffled standard money.
 *
 * The ledger defines the split once, per portion, with these properties:
 *
 * - Every dollar the solver allocated appears exactly once (per proposal and
 *   per funder totals equal the solver's).
 * - A portion's matching dollars sum to its matching budget: the funders'
 *   matching inflow (inflow × ratio), capped by what phase 2 actually added
 *   over phase 1. Solver totals are not budgets — either phase can leave
 *   money unallocated — so neither bound alone is right: matching the solver
 *   never placed cannot be pledged, and standard money phase 1 stranded that
 *   phase 2 recovered is standard, not matching. The budget is spread over
 *   the proposals whose allocation grew, in proportion to the growth — except
 *   that a proposal which did not request a matching pledge never carries
 *   matching: its growth is standard funding, and any matching budget that
 *   reached such proposals is reported as `matchingSpentAsStandard` rather
 *   than pledged.
 * - Preferred matching shares follow matching inflow (inflow × matching
 *   ratio); standard shares follow standard inflow plus each funder's share
 *   of unpledged matching. These shares are retained when they fit payer
 *   inflows and exclusions. Otherwise constrained attribution preserves those
 *   limits, including for budget experiments with a different phase split.
 * - Funder exclusions constrain who pays, without changing the portfolio.
 *   Attribution preserves every funder's inflow, including money the solver
 *   leaves unallocated, and places no dollars on a blocked edge. Reconciled
 *   exports supply both payments and matching labels from the solver: closeness
 *   to the preferred split precedes its final payment-attribution tie-breaker.
 *   The ledger validates and aggregates those labels without reassigning them.
 *
 * Exports and the API aggregate this ledger after reconciliation. Live ideal
 * previews reuse splitPortionAccounts through fundingPhases without assigning
 * payers. Fixed reconciled payments and phase labels take precedence here.
 */
import { formatCurrency } from "@lib/formatters";
import type NamedMatrix from "algorithm/NamedMatrix";
import {
  allocateEligibleFunding,
  allocateEligibleFundingWithMatching,
  fundingFlowTolerance,
} from "./eligibleFunding";

export type PhaseAmounts = { standard: number; matching: number };

export const ZERO_PHASE_AMOUNTS: Readonly<PhaseAmounts> = Object.freeze({
  standard: 0,
  matching: 0,
});

/** Read an aggregate, treating absent keys as zero dollars. */
export function ledgerAmounts(
  amountsByKey: { [key: string]: PhaseAmounts } | undefined,
  key: string,
): Readonly<PhaseAmounts> {
  return amountsByKey?.[key] ?? ZERO_PHASE_AMOUNTS;
}

export type FunderLedgerPortion = {
  id: string;
  recommenderId: string;
  funderIds: string[];
};

export type FunderLedgerInput = {
  portions: FunderLedgerPortion[];
  /** Phase-2 (with matching) accounts, one row per portion; hold column included. */
  portionAccounts: NamedMatrix<"portion", "proposal+hold">;
  /** Phase-1 (standard only) accounts, same shape as `portionAccounts`. */
  portionStandardAccounts: NamedMatrix<"portion", "proposal+hold">;
  /** Funder-layer accounts: how much of each funder's budget reached each recommender. */
  funderAccounts: NamedMatrix<"funder", "recommender+hold">;
  // Actual phase inflows from independent funder cost-sharing. Callers without
  // a funder-layer solve may still supply a proportional hypothetical split.
  standardFunderAccounts?: NamedMatrix<"funder", "recommender+hold">;
  matchingBudgetRatios: { [funderId: string]: number };
  /** Reconciliation fixes payments AND phases; neither may be reassigned here. */
  fixedPaymentsByPortion?: Record<
    string,
    { total: number[][]; matching: number[][] }
  >;
  /** Blocks restrict payer attribution, not the recommender's preferences. */
  excludedProposalsByFunder?: { [funderId: string]: string[] };
  requestsMatchingPledge: (proposalId: string) => boolean;
  holdProposalId: string;
};

export type FunderLedger = {
  /** Funder ids in the algorithm's output order. */
  funderIds: string[];
  /** Recommender ids in the algorithm's output order, hold excluded. */
  recommenderIds: string[];
  /** Proposal ids in the algorithm's column order, hold included. */
  proposalIds: string[];
  holdProposalId: string;
  byFunderRecommenderProposal: {
    [funderId: string]: {
      [recommenderId: string]: { [proposalId: string]: PhaseAmounts };
    };
  };
  byRecommenderProposal: {
    [recommenderId: string]: { [proposalId: string]: PhaseAmounts };
  };
  byFunderRecommender: {
    [funderId: string]: { [recommenderId: string]: PhaseAmounts };
  };
  byFunderProposal: {
    [funderId: string]: { [proposalId: string]: PhaseAmounts };
  };
  byProposal: { [proposalId: string]: PhaseAmounts };
  byFunder: { [funderId: string]: PhaseAmounts };
  /**
   * Matching budget that cannot be pledged: either the proposal did not
   * request matching, the standard benchmark already covers its payment, or
   * its eligible payer cannot supply matching. Matching pledged, this amount,
   * and unallocated matching together account for nominal matching inflow.
   */
  matchingSpentAsStandard: number;
  /** The part reclassified because matching cannot fit the eligible payments. */
  matchingRestrictedByExclusions: number;
  /**
   * Additional nominal standard funding placed beyond the standard phase's
   * allocation. It is standard funding, never matching.
   */
  recoveredStandard: number;
  /** Matching budget the matching phase left unallocated; it is not pledged. */
  unallocatedMatching: number;
  warnings: string[];
};

// Dollar discrepancies below this are float noise, not findings.
const LEDGER_TOLERANCE = 1;

export type PortionSplit = {
  amounts: PhaseAmounts[];
  /**
   * Matching budget available to pledge: the nominal matching inflow, capped
   * by what phase 2 actually added over phase 1.
   */
  matchingBudget: number;
  /** The part of `matchingBudget` that landed on pledge-requesting proposals. */
  matchingPledged: number;
  /** Phase-2 growth beyond the nominal matching inflow, before reconciling
   * the diagnostic with the final payer assignment. */
  recoveredStandard: number;
  /** Nominal matching inflow beyond phase-2 growth, before reconciling the
   * diagnostic with the final payer assignment. */
  unallocatedMatching: number;
};

/**
 * Split one portion's with-matching row into standard and matching dollars.
 * Matching is the portion's matching budget — `nominalMatchingBudget` (the
 * funders' inflow × ratio), capped by the realized growth from phase 1 to
 * phase 2 — spread over the pledge-requesting proposals whose allocation
 * grew, proportionally to the growth. The growth on pledge-requesting
 * proposals is always at least the budget that reached them, so no proposal's
 * matching exceeds its growth and no standard amount goes negative.
 */
export function splitPortionAccounts(
  standardRow: readonly number[],
  withMatchingRow: readonly number[],
  requestsMatchingPledge: readonly boolean[],
  nominalMatchingBudget: number,
): PortionSplit {
  let standardTotal = 0;
  let withMatchingTotal = 0;
  let pledgeableGrowth = 0;
  const growth = withMatchingRow.map((amount, index) => {
    const standardAmount = standardRow[index] ?? 0;
    standardTotal += standardAmount;
    withMatchingTotal += amount;
    const grew = Math.max(0, amount - standardAmount);
    if (requestsMatchingPledge[index]) {
      pledgeableGrowth += grew;
    }
    return grew;
  });

  const realizedGrowth = Math.max(0, withMatchingTotal - standardTotal);
  const nominal = Math.max(0, nominalMatchingBudget);
  const matchingBudget = Math.min(realizedGrowth, nominal);
  const scale =
    pledgeableGrowth > 0 ? Math.min(1, matchingBudget / pledgeableGrowth) : 0;
  const amounts = withMatchingRow.map((amount, index) => {
    const matching = requestsMatchingPledge[index]
      ? (growth[index] ?? 0) * scale
      : 0;
    return { standard: amount - matching, matching };
  });

  return {
    amounts,
    matchingBudget,
    matchingPledged: pledgeableGrowth * scale,
    recoveredStandard: Math.max(0, realizedGrowth - nominal),
    unallocatedMatching: Math.max(0, nominal - realizedGrowth),
  };
}

function addAmounts(
  target: { [key: string]: PhaseAmounts },
  key: string,
  amounts: PhaseAmounts,
): void {
  const entry = (target[key] ??= { standard: 0, matching: 0 });
  entry.standard += amounts.standard;
  entry.matching += amounts.matching;
}

/** Keep the portfolio and funder budgets fixed; phase labels follow eligible payments. */
function attributeEligiblePayments({
  portionId,
  inflows,
  matchingInflows,
  matchingBudget,
  matchingGrowth,
  proposalAmounts,
  preferred,
  eligible,
}: {
  portionId: string;
  inflows: number[];
  matchingInflows: number[];
  matchingBudget: number;
  matchingGrowth: number[];
  proposalAmounts: PhaseAmounts[];
  preferred: PhaseAmounts[][];
  eligible: boolean[][];
}): PhaseAmounts[][] {
  const demands = proposalAmounts.map(
    (amounts) => amounts.standard + amounts.matching,
  );
  const totalInflow = inflows.reduce((sum, inflow) => sum + inflow, 0);
  const totalAllocated = demands.reduce((sum, amount) => sum + amount, 0);
  // An internal unallocated column preserves every funder's budget without
  // reporting money the recommender never placed as an actual payment.
  const unallocatedIndex = demands.length;
  const totalPayments = allocateEligibleFunding({
    supplies: inflows,
    demands: [...demands, Math.max(0, totalInflow - totalAllocated)],
    eligible: eligible.map((row) => [...row, true]),
    preferred: preferred.map((row, index) => {
      const amounts = row.map((edge) => edge.standard + edge.matching);
      return [
        ...amounts,
        Math.max(
          0,
          inflows[index]! - amounts.reduce((sum, amount) => sum + amount, 0),
        ),
      ];
    }),
    holdIndex: unallocatedIndex,
  });
  if (
    totalPayments.unmetDemand.some((amount) => amount > totalPayments.tolerance)
  ) {
    // The recommender layer must cap and rerun an unaffordable portfolio.
    // Silently renormalizing attribution here would overcharge a funder or
    // drop already allocated grants from exports.
    throw new Error(
      `Cannot attribute ${portionId}'s portfolio within eligible funder budgets`,
    );
  }

  // Keep a feasible preferred assignment when it already preserves all possible
  // matching. Otherwise solve payments and phase labels together: freezing an
  // arbitrary total assignment can discard a pledge another payer split funds.
  const matchingPayments = allocateEligibleFunding({
    supplies: matchingInflows,
    demands: proposalAmounts.map((amounts) => amounts.matching),
    eligible,
    preferred: preferred.map((row) => row.map((amounts) => amounts.matching)),
    capacities: totalPayments.allocations.map((row) =>
      row.slice(0, unallocatedIndex),
    ),
  });
  const optimum = allocateEligibleFundingWithMatching({
    supplies: inflows,
    demands: [...demands, Math.max(0, totalInflow - totalAllocated)],
    eligible: eligible.map((row) => [...row, true]),
    matchingSupplies: matchingInflows,
    matchingDemands: [...matchingGrowth, 0],
    holdIndex: unallocatedIndex,
  });
  if (optimum.unmetDemand.some((amount) => amount > optimum.tolerance)) {
    throw new Error(`Cannot assign payment phases for ${portionId}`);
  }
  const sumPayments = (rows: number[][]) =>
    rows.reduce(
      (sum, row) =>
        sum + row.reduce((subtotal, amount) => subtotal + amount, 0),
      0,
    );
  // Realized growth limits matching globally. Reducing every payer's capacity
  // first could discard a pledge that its sole eligible payer can fully cover.
  // Scale labels only; the feasible total payments remain unchanged.
  const optimumMatchingTotal = sumPayments(optimum.matchingAllocations);
  const matchingScale =
    optimumMatchingTotal > 0
      ? Math.min(1, matchingBudget / optimumMatchingTotal)
      : 0;
  const useOptimum =
    Math.min(matchingBudget, optimumMatchingTotal) >
    sumPayments(matchingPayments.allocations) +
      Math.max(optimum.tolerance, matchingPayments.tolerance);
  const totals = useOptimum ? optimum.allocations : totalPayments.allocations;
  const matches = useOptimum
    ? optimum.matchingAllocations.map((row) =>
        row.map((amount) => amount * matchingScale),
      )
    : matchingPayments.allocations;
  return totals.map((row, index) =>
    demands.map((_, col) => {
      const matching = matches[index]![col]!;
      return { standard: row[col]! - matching, matching };
    }),
  );
}

export function buildFunderLedger(input: FunderLedgerInput): FunderLedger {
  const {
    portions,
    portionAccounts,
    portionStandardAccounts,
    funderAccounts,
    matchingBudgetRatios,
    holdProposalId,
  } = input;
  const proposalIds = portionAccounts.colNames;
  const requestsMatchingPledge = proposalIds.map((proposalId) =>
    input.requestsMatchingPledge(proposalId),
  );

  const ledger: FunderLedger = {
    funderIds: funderAccounts.rowNames,
    recommenderIds: funderAccounts.colNames.filter(
      (recommenderId) => recommenderId !== holdProposalId,
    ),
    proposalIds,
    holdProposalId,
    byFunderRecommenderProposal: {},
    byRecommenderProposal: {},
    byFunderRecommender: {},
    byFunderProposal: {},
    byProposal: {},
    byFunder: {},
    matchingSpentAsStandard: 0,
    matchingRestrictedByExclusions: 0,
    recoveredStandard: 0,
    unallocatedMatching: 0,
    warnings: [],
  };

  // Money a funder parks on hold never enters the recommender layer, so no
  // portion carries it. Record it under the hold "recommender" split by the
  // funder's matching ratio, so funder-level aggregates cover the funder's
  // whole allocation.
  for (const funderId of ledger.funderIds) {
    const onHold = funderAccounts.getNames(funderId, holdProposalId);
    if (onHold === 0) {
      continue;
    }
    const matching = input.standardFunderAccounts
      ? onHold - input.standardFunderAccounts.getNames(funderId, holdProposalId)
      : onHold * (matchingBudgetRatios[funderId] ?? 0);
    const amounts = { standard: onHold - matching, matching };
    addAmounts(
      (ledger.byFunderRecommender[funderId] ??= {}),
      holdProposalId,
      amounts,
    );
    addAmounts(ledger.byFunder, funderId, amounts);
  }

  for (const portion of portions) {
    if (portion.funderIds.length === 0) {
      continue;
    }

    // Cost-sharing determines the phase inflows independently. Applying the
    // funder's nominal ratio here would erase the matching commitment again.
    const inflows = portion.funderIds.map((funderId) =>
      funderAccounts.getNames(funderId, portion.recommenderId),
    );
    const matchingInflows = inflows.map((inflow, index) =>
      input.standardFunderAccounts
        ? inflow -
          input.standardFunderAccounts.getNames(
            portion.funderIds[index]!,
            portion.recommenderId,
          )
        : inflow * (matchingBudgetRatios[portion.funderIds[index]!] ?? 0),
    );
    const totalInflow = inflows.reduce((sum, inflow) => sum + inflow, 0);
    if (totalInflow <= 0) {
      continue;
    }
    const totalMatchingInflow = matchingInflows.reduce(
      (sum, inflow) => sum + inflow,
      0,
    );

    const split = splitPortionAccounts(
      portionStandardAccounts.getRowName(portion.id).toArray(),
      portionAccounts.getRowName(portion.id).toArray(),
      requestsMatchingPledge,
      totalMatchingInflow,
    );
    // Matching budget that reached non-requesting proposals is paid out as
    // standard funding, but it is still the matching funders' money.
    const unpledgedMatching = split.matchingBudget - split.matchingPledged;
    const matchingShares = matchingInflows.map((inflow) =>
      totalMatchingInflow > 0 ? inflow / totalMatchingInflow : 0,
    );
    const standardWeights = inflows.map(
      (inflow, index) =>
        inflow -
        (matchingInflows[index] ?? 0) +
        unpledgedMatching * (matchingShares[index] ?? 0),
    );
    const totalStandardWeight = standardWeights.reduce(
      (sum, weight) => sum + weight,
      0,
    );
    const standardShares =
      totalStandardWeight > 0
        ? standardWeights.map((weight) => weight / totalStandardWeight)
        : inflows.map((inflow) => inflow / totalInflow);

    const preferred = portion.funderIds.map((_, index) =>
      split.amounts.map((edge) => ({
        standard: edge.standard * (standardShares[index] ?? 0),
        matching: edge.matching * (matchingShares[index] ?? 0),
      })),
    );
    const eligible = portion.funderIds.map((funderId) => {
      const excluded = new Set(
        input.excludedProposalsByFunder?.[funderId] ?? [],
      );
      return proposalIds.map(
        (proposalId) =>
          proposalId === holdProposalId || !excluded.has(proposalId),
      );
    });
    let attributed = preferred;
    const tolerance = fundingFlowTolerance(
      inflows,
      split.amounts.map((amounts) => amounts.standard + amounts.matching),
    );
    // Budget experiments can retain a standard-phase basis that differs from
    // the actual payer mix. Proportional phase shares are only a preference;
    // they must fit each payer's inflow even when every grant is eligible.
    const fixed = input.fixedPaymentsByPortion?.[portion.id];
    if (fixed) {
      if (
        fixed.total.length !== inflows.length ||
        fixed.matching.length !== inflows.length ||
        fixed.total.some(
          (row, f) =>
            row.length !== proposalIds.length ||
            fixed.matching[f]?.length !== row.length ||
            row.some((total, p) => {
              const matching = fixed.matching[f]![p]!;
              return (
                !Number.isFinite(total) ||
                total < 0 ||
                !Number.isFinite(matching) ||
                matching < 0 ||
                matching > total + tolerance ||
                (!eligible[f]![p] && total > tolerance) ||
                (!requestsMatchingPledge[p] && matching > tolerance)
              );
            }) ||
            row.reduce((a, b) => a + b, 0) > inflows[f]! + tolerance ||
            fixed.matching[f]!.reduce((a, b) => a + b, 0) >
              matchingInflows[f]! + tolerance,
        ) ||
        proposalIds.some(
          (_, p) =>
            Math.abs(
              fixed.total.reduce((sum, row) => sum + row[p]!, 0) -
                portionAccounts.getRowName(portion.id).get(p),
            ) > tolerance,
        )
      )
        throw new Error(`Invalid reconciled phases for ${portion.id}`);
      attributed = fixed.total.map((row, f) =>
        row.map((total, p) => ({
          standard: Math.max(0, total - fixed.matching[f]![p]!),
          matching: fixed.matching[f]![p]!,
        })),
      );
    } else if (
      preferred.some(
        (row, index) =>
          row.reduce(
            (sum, amounts) => sum + amounts.standard + amounts.matching,
            0,
          ) >
            inflows[index]! + tolerance ||
          row.some(
            (amounts, col) =>
              !eligible[index]![col] && amounts.standard + amounts.matching > 0,
          ),
      )
    ) {
      attributed = attributeEligiblePayments({
        portionId: portion.id,
        inflows,
        matchingInflows,
        matchingBudget: split.matchingBudget,
        matchingGrowth: proposalIds.map((_, col) =>
          requestsMatchingPledge[col]
            ? Math.max(
                0,
                portionAccounts.getRowName(portion.id).get(col) -
                  portionStandardAccounts.getRowName(portion.id).get(col),
              )
            : 0,
        ),
        proposalAmounts: split.amounts,
        preferred,
        eligible,
      });
    }

    // Phase growth suggests how much matching became standard, but the final
    // payers determine whether that money was spent at all. Keep the suggested
    // split whenever it fits: each payer's standard payment must use matching
    // above their nominal standard inflow, and cannot use matching beyond
    // either the payment itself or their remaining matching inflow.
    let standardPaid = 0;
    let matchingPledged = 0;
    let minimumConverted = 0;
    let maximumConverted = 0;
    for (let index = 0; index < attributed.length; index++) {
      let payerStandard = 0;
      let payerMatching = 0;
      for (const amounts of attributed[index]!) {
        payerStandard += amounts.standard;
        payerMatching += amounts.matching;
      }
      standardPaid += payerStandard;
      matchingPledged += payerMatching;
      minimumConverted += Math.max(
        0,
        payerStandard - (inflows[index]! - matchingInflows[index]!),
      );
      maximumConverted += Math.min(
        payerStandard,
        Math.max(0, matchingInflows[index]! - payerMatching),
      );
    }
    const matchingSpentAsStandard = Math.min(
      maximumConverted,
      Math.max(minimumConverted, split.matchingBudget - matchingPledged),
    );
    ledger.matchingSpentAsStandard += matchingSpentAsStandard;
    ledger.matchingRestrictedByExclusions += Math.min(
      matchingSpentAsStandard,
      Math.max(0, split.matchingPledged - matchingPledged),
    );
    ledger.unallocatedMatching += Math.max(
      0,
      totalMatchingInflow - matchingPledged - matchingSpentAsStandard,
    );
    ledger.recoveredStandard += Math.max(
      0,
      standardPaid -
        matchingSpentAsStandard -
        portionStandardAccounts.getRowName(portion.id).sum(),
    );

    for (let col = 0; col < proposalIds.length; col++) {
      const proposalId = proposalIds[col]!;
      const edge = split.amounts[col]!;
      if (edge.standard === 0 && edge.matching === 0) {
        continue;
      }
      for (let index = 0; index < portion.funderIds.length; index++) {
        const funderId = portion.funderIds[index]!;
        const amounts = attributed[index]![col]!;
        if (amounts.standard === 0 && amounts.matching === 0) {
          continue;
        }
        const byRecommender = (ledger.byFunderRecommenderProposal[funderId] ??=
          {});
        addAmounts(
          (byRecommender[portion.recommenderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(
          (ledger.byRecommenderProposal[portion.recommenderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(
          (ledger.byFunderRecommender[funderId] ??= {}),
          portion.recommenderId,
          amounts,
        );
        addAmounts(
          (ledger.byFunderProposal[funderId] ??= {}),
          proposalId,
          amounts,
        );
        addAmounts(ledger.byProposal, proposalId, amounts);
        addAmounts(ledger.byFunder, funderId, amounts);
      }
    }
  }

  if (ledger.recoveredStandard > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.recoveredStandard)} of additional standard funding was allocated during the matching phase.`,
    );
  }
  if (ledger.unallocatedMatching > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.unallocatedMatching)} of matching budget was left unallocated by recommenders and is not pledged.`,
    );
  }
  const otherMatchingSpentAsStandard =
    ledger.matchingSpentAsStandard - ledger.matchingRestrictedByExclusions;
  if (otherMatchingSpentAsStandard > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(otherMatchingSpentAsStandard)} of matching budget was paid as standard funding.`,
    );
  }
  if (ledger.matchingRestrictedByExclusions > LEDGER_TOLERANCE) {
    ledger.warnings.push(
      `${formatCurrency(ledger.matchingRestrictedByExclusions)} of matching budget could not be pledged within the eligible funders' payments and is counted as standard funding.`,
    );
  }

  return ledger;
}
